import React, { useState } from 'react';
import { Formik, FormikProps } from 'formik';
import { Form } from 'formik-antd/lib/form';
import {
  AnyAssetApplication,
  APIAsset,
  AssetParams,
  Connections,
  ConnectionTypes,
  createUrl,
  Flex,
  FormikFormAsset,
  FormikFormConnectionKey,
  FormikNewFormValues,
  getCustomFieldsSchema,
  Loading,
  NewAsset,
  NONE_SELECT,
  openNotificationWithIcon,
  PageContent,
  StepKeys,
  Stepper,
  useAssetConfig,
  useAssetParams,
  useAssetTranslation,
} from '@mss/mss-component-library';
import AssetCollectors from './AssetCollectors';
import * as yup from 'yup';
import { object } from 'yup';
import Button from '../../Button';
import SubmitButton from '../../SubmitButton';
import Connection from '../Shared/Connection';
import Settings from './Settings';

import ContinueFailedTestButton from './ContinueFailedTestButton';
import Fetch from '../../util/_fetch';
import { notification } from 'antd';
import { assetModel, paramsToExportAssetType } from '../Import/util';
import { useHistory } from 'react-router-dom';
import { sanitizeAssetForPost } from '../utils/sanitize';
import { useCustomFieldsQuery } from '../../../asset/services/queries';
import { useQueryClient } from '@tanstack/react-query';
import { emptyConnectionTest } from '../utils/emptyConnectionTest';

function ConfigureNewAsset<AssetType extends AnyAssetApplication>({
  numberOfAssets = 1,
  overrideAssetParams,
}: {
  numberOfAssets?: number;
  overrideAssetParams?: Partial<AssetParams>;
}) {
  const queryClient = useQueryClient();
  const { assetSubType, assetType, category } =
    useAssetParams(overrideAssetParams);
  const { data: customFields, isLoading: isLoadingCustomFields } =
    useCustomFieldsQuery(assetModel[paramsToExportAssetType(assetType)]);

  const { matchingConfig } = useAssetConfig<AssetType>(assetSubType);
  const history = useHistory();

  const [newAsset, setNewAsset] = useState<APIAsset<AssetType>>();

  const [currentStep, setCurrentStep] = useState<StepKeys>(
    matchingConfig.steps[0].value
  );
  const { t } = useAssetTranslation();

  const nextStep = () =>
    setCurrentStep(matchingConfig.steps[currentStepIndex + 1].value);

  const prevStep = () =>
    setCurrentStep(matchingConfig.steps[currentStepIndex - 1].value);

  const returnToList = () =>
    history.push(
      `/${category}${category.endsWith('s') ? '' : 's'}/${assetType}`
    );

  const successNotification = (results: AnyAssetApplication[]) =>
    openNotificationWithIcon(
      notification.success,
      t(
        `((fullAssetName)) ${
          results.length > 1 ? 'were' : 'was'
        } added successfully.`,
        {
          fullAssetName: matchingConfig.name + (results.length > 1 ? 's' : ''),
        }
      ),
      ''
    );

  const failedMultipleNotification = (results: any[]) =>
    openNotificationWithIcon(
      notification.error,
      t('((number)) ((category)) could not be added.', {
        number: results.length,
        category: matchingConfig.name + (results.length > 1 ? 's' : ''),
      }),
      ''
    );

  const failedSingleNotification = (results: any[]) =>
    openNotificationWithIcon(
      notification.error,
      t('((category)) could not be added.', {
        category: matchingConfig.name + (results.length > 1 ? 's' : ''),
      }),
      ''
    );

  const handleSubmit = async (values: FormikNewFormValues<AssetType>) => {
    const afterOnboardingStep = matchingConfig.steps.find(
      x => x.value === 'afterOnboarding'
    );

    const hasExtraStep =
      afterOnboardingStep &&
      (afterOnboardingStep.show == null ||
        afterOnboardingStep.show({ values } as FormikProps<any>));

    try {
      const responses = await Promise.all<Response>(
        values.assets.map(({ values: asset }) => {
          return Fetch.post(createUrl({ assetType, assetSubType }), {
            body: JSON.stringify(
              sanitizeAssetForPost(
                matchingConfig.form.submitDataParser?.(asset) || asset
              )
            ),
          });
        })
      );

      const results = await Promise.all<APIAsset<AssetType>>(
        responses.map(response => response.json())
      );

      if (responses.some(x => !x.ok)) {
        // @ts-ignore
        throw new Error('Adding (partially) failed', { cause: results });
      }

      successNotification(results);
      await queryClient.invalidateQueries({
        queryKey: [assetType, assetSubType],
      });
      if (!hasExtraStep) {
        returnToList();
      } else {
        nextStep();
        setNewAsset(results[0]);
      }
    } catch (e: any) {
      console.error('Error', e, e.cause);
      if (e?.cause && Array.isArray(e.cause)) {
        const { error, success } = e.cause.reduce(
          (acc: any, x: any) => {
            if (x.id) {
              acc.success.push(x);
            } else {
              acc.error.push(x);
            }
            return acc;
          },
          {
            error: [],
            success: [],
          }
        );

        if (e.cause.length > 1) {
          failedMultipleNotification(error);
        } else {
          failedSingleNotification(error);
        }

        if (success.length > 0) {
          successNotification(success);
        }
      } else {
        failedSingleNotification(values.assets);
      }
    }
  };
  if (!matchingConfig) return null;
  const currentStepIndex = matchingConfig.steps.findIndex(
    step => step.value === currentStep
  );

  const notFirstStep = currentStepIndex > 0;
  const notLastStepBeforeSubmit =
    currentStepIndex <
    matchingConfig.steps.filter(x => x.value !== 'afterOnboarding').length - 1;
  const lastStepBeforeSubmit =
    currentStepIndex ===
    matchingConfig.steps.filter(x => x.value !== 'afterOnboarding').length - 1;

  const connectSchemas = Object.values(matchingConfig.form.connect).reduce(
    (acc, config) => (config.schema ? acc.concat(config.schema) : acc),
    object()
  );

  const connectionTypes = Object.keys(matchingConfig.form.connect) as Array<
    keyof Connections
  >;
  const alwaysOnConfigs = connectionTypes.filter(
    x => !matchingConfig.form.connect[x]?.select
  );

  const schemas = object()
    .concat(matchingConfig.form.configure.schema)
    .concat(connectSchemas)
    .concat(object(getCustomFieldsSchema(t, customFields)));

  return (
    <>
      {isLoadingCustomFields && (
        <PageContent
          title={
            <Stepper
              disabled={currentStep === 'afterOnboarding'}
              currentStep={{
                index: currentStepIndex,
                set: (i: any) => setCurrentStep(matchingConfig.steps[i].value),
              }}
              steps={matchingConfig.steps.filter(x => !x.show)}
            />
          }
        >
          <Loading />
        </PageContent>
      )}
      {!isLoadingCustomFields && (
        <Formik<FormikNewFormValues<AssetType>>
          initialValues={{
            assets: Array.from({ length: numberOfAssets }, (_, tempId) => {
              const base = {
                values: { ...matchingConfig.form.initialValues, tempId },
              };

              return Object.keys(matchingConfig.form.connect).reduce(
                (acc, key) => {
                  const selectConfig =
                    matchingConfig.form.connect[key as ConnectionTypes]?.select;

                  return {
                    ...acc,
                    values: {
                      ...acc.values,
                      ...(selectConfig && selectConfig?.nullable
                        ? { [selectConfig.key]: NONE_SELECT }
                        : {}),
                    },
                    [`test_${key}`]: emptyConnectionTest,
                  };
                },
                base as FormikFormAsset<NewAsset<AssetType>>
              );
            }),
          }}
          onSubmit={handleSubmit}
          validationSchema={yup.object().shape({
            assets: yup.array().of(yup.object().shape({ values: schemas })),
          })}
        >
          {formikProps => {
            const hasAnyTestRunning = connectionTypes.some(
              key =>
                formikProps.values.assets[0][
                  `test_${key}` as FormikFormConnectionKey
                ].loading
            );

            const alwaysOnConfigNotRan = alwaysOnConfigs.some(
              key =>
                formikProps.values.assets[0][
                  `test_${key}` as FormikFormConnectionKey
                ].result.status.port === null
            );

            const connectionIsValid = connectSchemas.isValidSync(
              formikProps.values.assets[0].values
            );

            const enabledSteps = matchingConfig.steps.filter(
              step => !step.show || step.show(formikProps)
            );
            const lastStep = currentStepIndex === enabledSteps.length - 1;
            return (
              <Form>
                <PageContent
                  title={
                    <Stepper
                      disabled={currentStep === 'afterOnboarding'}
                      currentStep={{
                        index: currentStepIndex,
                        set: (i: any) =>
                          setCurrentStep(matchingConfig.steps[i].value),
                      }}
                      steps={enabledSteps}
                    />
                  }
                >
                  {enabledSteps.map(step => {
                    if (step.value !== currentStep) return null;
                    const key = `${step.value}-${step.label}`;
                    switch (currentStep) {
                      case 'collectors':
                        return <AssetCollectors<AssetType> key={key} />;
                      case 'connect':
                        return (
                          <Connection<AssetType>
                            key={key}
                            disabled={hasAnyTestRunning}
                          />
                        );
                      case 'configure':
                        return <Settings<AssetType> key={key} />;
                      case 'afterOnboarding':
                        return newAsset
                          ? enabledSteps
                              .find(x => x.value === currentStep)
                              ?.render?.(newAsset as any)
                          : null;

                      default:
                        return <div key={key}>invalid step</div>;
                    }
                  })}
                  <Flex
                    h={
                      currentStep !== 'afterOnboarding' &&
                      enabledSteps.findIndex(step => step.value === currentStep)
                        ? 'between'
                        : 'end'
                    }
                    v="center"
                    style={{ marginTop: '1rem' }}
                  >
                    {notFirstStep && currentStep !== 'afterOnboarding' && (
                      <Button data-ta="previousStepButton" onClick={prevStep}>
                        {t('Previous')}
                      </Button>
                    )}
                    {lastStepBeforeSubmit && (
                      <SubmitButton>
                        {lastStep
                          ? t('Complete')
                          : t('Onboard and setup')}
                      </SubmitButton>
                    )}
                    {(['connect', 'afterOnboarding'].includes(currentStep) ||
                      notLastStepBeforeSubmit) && (
                      <span>
                        {currentStep === 'connect' && (
                          <ContinueFailedTestButton
                            disabled={
                              hasAnyTestRunning ||
                              alwaysOnConfigNotRan ||
                              !connectionIsValid
                            }
                            onClick={nextStep}
                          />
                        )}
                        {(notLastStepBeforeSubmit ||
                          currentStep === 'afterOnboarding') && (
                          <Button
                            onClick={
                              currentStep === 'afterOnboarding'
                                ? returnToList
                                : nextStep
                            }
                            disabled={matchingConfig.steps[
                              currentStepIndex
                            ].disabledNextStep?.(formikProps as any, 1)}
                            variant="primary"
                            data-ta="nextStepButton"
                          >
                            {lastStep
                              ? t('Complete')
                              : t('Next step')}
                          </Button>
                        )}
                      </span>
                    )}
                  </Flex>
                </PageContent>
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
}

export default ConfigureNewAsset;
